import React, { useState, useEffect, useCallback } from 'react';
import { Users, Trash2 } from 'lucide-react';
import { Card, Button, Pill, Input, Select, Label, FieldGroup } from '../';
import { useToast } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';
import * as api from '../../../services/api';

/**
 * AccessPanel — the Access journey step (agent role grants) for ALL agent types.
 *
 * Originally extracted from ExteletActTab's "Access grants" block (J1b) as the
 * ExteLet RBAC step; J1c generalizes it to native Lets / AutoLets / SysLets too
 * (the grants are agent-scoped, identical across types). Component renamed from
 * ExteletAccessPanel → AccessPanel; the file path is kept to avoid build churn.
 * The grant/remove logic, API calls, and markup are unchanged.
 *
 * The `assignment_present` Execution-Ready check is still server-evaluated;
 * after a grant/remove we fire {@link props.onChanged} so the Promote step's
 * readiness gate refreshes the next time it's viewed (it also reloads on its
 * own mount, so the panels stay decoupled).
 *
 * @param {Object}   props
 * @param {string}   props.agentId
 * @param {Function} [props.onChanged]  called after a successful grant/remove
 */
export default function AccessPanel({ agentId, onChanged }) {
  const toastCtx = useToast();
  const addToast = useCallback((arg) => {
    if (toastCtx && typeof toastCtx.addToast === 'function') return toastCtx.addToast(arg);
    // eslint-disable-next-line no-console
    console.warn('[AccessPanel] toast unavailable, dropped:', arg);
    return null;
  }, [toastCtx]);
  const auth = useAuth();
  const currentUserId = auth?.session?.userId;

  const [assignments, setAssignments] = useState([]);
  const [assignLoading, setAssignLoading] = useState(true);
  const [assignKind, setAssignKind] = useState('user');
  const [assignTargetId, setAssignTargetId] = useState('');
  const [assignAccess, setAssignAccess] = useState('execute');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const loadAssignments = useCallback(async () => {
    if (!agentId) { setAssignLoading(false); return; }
    setAssignLoading(true);
    try {
      const res = await api.getAssignments(agentId);
      const items = Array.isArray(res?.data?.items) ? res.data.items
                  : Array.isArray(res?.data) ? res.data
                  : Array.isArray(res?.items) ? res.items : [];
      setAssignments(items);
    } catch (e) {
      setAssignments([]);
    } finally {
      setAssignLoading(false);
    }
  }, [agentId]);

  const notifyChanged = useCallback(() => {
    if (typeof onChanged === 'function') {
      try { onChanged(); } catch { /* parent refresh is best-effort */ }
    }
  }, [onChanged]);

  const addAssignment = useCallback(async () => {
    if (!agentId || !assignTargetId.trim()) {
      addToast({ tone: 'danger', title: 'Target required', description: 'Enter the user or group ID to grant access.' });
      return;
    }
    setAdding(true);
    try {
      const payload = { accessLevel: assignAccess, assignedBy: currentUserId };
      if (assignKind === 'user') payload.userId = assignTargetId.trim();
      else payload.groupId = assignTargetId.trim();
      const res = await api.createAssignment(agentId, payload);
      if (!res || res.ok === false) throw new Error(res?.error || res?.data?.error || 'Assignment rejected');
      setAssignTargetId('');
      addToast({ tone: 'success', title: 'Access granted' });
      await loadAssignments();
      notifyChanged();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not grant access', description: e?.message || String(e) });
    } finally {
      setAdding(false);
    }
  }, [agentId, assignTargetId, assignKind, assignAccess, currentUserId, addToast, loadAssignments, notifyChanged]);

  const removeAssignment = useCallback(async (assignmentId) => {
    if (!agentId || !assignmentId) return;
    setRemovingId(assignmentId);
    try {
      const res = await api.removeAssignment(agentId, assignmentId);
      if (res && res.ok === false) throw new Error(res.error || 'Remove failed');
      await loadAssignments();
      notifyChanged();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not remove', description: e?.message || String(e) });
    } finally {
      setRemovingId(null);
    }
  }, [agentId, addToast, loadAssignments, notifyChanged]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  return (
    <Card padding="none">
      <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
        <Users size={16} className="text-v2-cyan" />
        <h3 className="text-[14px] font-bold tracking-tight m-0">Access</h3>
        <span className="text-[11px] text-v2-ink-muted">{assignments.length} grant(s)</span>
      </div>
      <div className="p-4 space-y-3">
        {assignLoading ? (
          <p className="text-[12px] text-v2-ink-muted m-0">Loading access grants…</p>
        ) : assignments.length === 0 ? (
          <p className="text-[12px] text-v2-ink-muted m-0">No access grants yet. Grant at least one user or group below.</p>
        ) : (
          <ul className="space-y-1.5 list-none p-0 m-0">
            {assignments.map((a) => (
              <li key={a.id} className="flex items-center gap-3 text-[12px] rounded-v2-sm bg-v2-muted px-3 py-2">
                <Pill tone="muted" size="sm">{a.userId ? 'User' : 'Group'}</Pill>
                <span className="flex-1 min-w-0 truncate text-v2-ink">{a.userName || a.groupName || a.userId || a.groupId}</span>
                <span className="text-[10.5px] text-v2-ink-muted">{a.accessLevel}</span>
                <button type="button" onClick={() => removeAssignment(a.id)} disabled={removingId === a.id}
                  className="v2-focus-ring text-v2-ink-muted hover:text-v2-danger" title="Remove">
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="grid gap-2 items-end" style={{ gridTemplateColumns: 'auto 1fr auto auto' }}>
          <FieldGroup>
            <Label htmlFor="assign-kind">Kind</Label>
            <Select id="assign-kind" value={assignKind} onChange={(e) => setAssignKind(e.target.value)}>
              <option value="user">User</option>
              <option value="group">Group</option>
            </Select>
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="assign-target">{assignKind === 'user' ? 'User ID' : 'Group ID'}</Label>
            <Input id="assign-target" value={assignTargetId}
              onChange={(e) => setAssignTargetId(e.target.value)} placeholder="UUID" />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="assign-access">Access</Label>
            <Select id="assign-access" value={assignAccess} onChange={(e) => setAssignAccess(e.target.value)}>
              <option value="execute">Execute</option>
              <option value="view">View</option>
              <option value="manage">Manage</option>
            </Select>
          </FieldGroup>
          <Button variant="secondary" size="sm" loading={adding} onClick={addAssignment}>Grant</Button>
        </div>
      </div>
    </Card>
  );
}
